import React from 'react';
import { IonCard, IonCardContent, IonIcon, IonSkeletonText } from '@ionic/react';
import { rainyOutline, snowOutline, sunnyOutline, waterOutline, swapVerticalOutline } from 'ionicons/icons';
import { monthLabel, round1, shortDate, type StationAgro as Agro } from '../../../domain/WeatherStation';
import { useStationAgro } from '../../../application/use-cases/weatherStation/useStationAgro';
import Sparkline from './Sparkline';

const WET_DAYS_SHOWN = 14;
const FROSTS_SHOWN = 5;
// El backend acepta hasta 400 días hacia atrás.
const MAX_DAYS_BACK = 399;

function minSince(today: string): string {
  const [y, m, d] = today.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - MAX_DAYS_BACK)).toISOString().slice(0, 10);
}

function wetHoursOf(agro: Agro, offsetFromEnd: number): number | null {
  return agro.days[agro.days.length - 1 - offsetFromEnd]?.wetHours ?? null;
}

const signed = (n: number) => `${n > 0 ? '+' : ''}${round1(n)}`;

// Indicadores agronómicos, desde una fecha (la
// siembra o el inicio de campaña), en su orden de importancia: lluvia de la
// campaña, heladas, grados día, horas de hoja mojada (riesgo de hongos) y
// balance de agua (lluvia − evapotranspiración).
const StationAgro: React.FC<{ deviceId: string; hasAir: boolean }> = ({ deviceId, hasAir }) => {
  const { agro, error, settings, setSettings, today } = useStationAgro(deviceId);

  const wetDays = agro ? agro.days.slice(-WET_DAYS_SHOWN) : [];
  const wetTotal7 = agro ? agro.days.slice(-7).reduce((sum, d) => sum + (d.wetHours ?? 0), 0) : 0;
  const frosts = agro ? [...agro.frosts].reverse() : [];
  const maxMonthMm = agro?.rain ? Math.max(1, ...agro.rain.monthly.map((m) => m.mm)) : 1;

  return (
    <IonCard className="station-agro-card">
      <IonCardContent>
        <div className="station-section-head">
          <span>Campo</span>
          <span className="station-source">Calculado con los datos de la estación</span>
        </div>

        <div className="station-agro-settings">
          <label>
            Desde
            <input
              type="date"
              value={settings.since}
              min={minSince(today)}
              max={today}
              onChange={(e) => e.target.value && setSettings({ ...settings, since: e.target.value })}
            />
          </label>
          {hasAir && (
            <label>
              Temp. base
              <span className="station-agro-base">
                <input
                  type="number"
                  inputMode="decimal"
                  step={0.5}
                  min={-5}
                  max={20}
                  value={settings.baseTemp}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (e.target.value !== '' && value >= -5 && value <= 20) setSettings({ ...settings, baseTemp: value });
                  }}
                />
                °C
              </span>
            </label>
          )}
        </div>

        {!agro && !error && <IonSkeletonText animated style={{ width: '100%', height: 180, borderRadius: 10, margin: 0 }} />}
        {error && !agro && <p className="station-empty">{error}</p>}

        {agro && (
          <div className="station-agro-blocks">
            {agro.rain && (
              <div className="station-agro-block metric-rain">
                <span className="station-agro-icon">
                  <IonIcon icon={rainyOutline} />
                </span>
                <div className="station-agro-body">
                  <p className="station-agro-title">Lluvia acumulada</p>
                  <p className="station-agro-value">
                    {round1(agro.rain.total)}
                    <small> mm desde el {shortDate(agro.since)}</small>
                  </p>
                  <ul className="station-agro-months">
                    {agro.rain.monthly.map((m) => (
                      <li key={m.month}>
                        <span>{monthLabel(m.month)}</span>
                        <span className="station-agro-month-bar">
                          <span style={{ width: `${(m.mm / maxMonthMm) * 100}%` }} />
                        </span>
                        <strong>{round1(m.mm)} mm</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {hasAir && (
              <div className={`station-agro-block ${frosts.length ? 'level-frost' : ''}`}>
                <span className="station-agro-icon">
                  <IonIcon icon={snowOutline} />
                </span>
                <div className="station-agro-body">
                  <p className="station-agro-title">Heladas</p>
                  <p className="station-agro-value">
                    {frosts.length}
                    <small> desde el {shortDate(agro.since)}</small>
                  </p>
                  {frosts.length === 0 ? (
                    <p className="station-agro-detail">Sin heladas registradas en la estación.</p>
                  ) : (
                    <ul className="station-agro-frosts">
                      {frosts.slice(0, FROSTS_SHOWN).map((f) => (
                        <li key={f.night}>
                          <span>Noche del {shortDate(f.night)}</span>
                          <strong>{round1(f.minC)}°</strong>
                          <span>{f.hours} h</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="station-agro-detail">A la altura del cultivo suele hacer 2 a 4 °C menos que en la estación.</p>
                </div>
              </div>
            )}

            {hasAir && (
              <div className="station-agro-block metric-temp">
                <span className="station-agro-icon">
                  <IonIcon icon={sunnyOutline} />
                </span>
                <div className="station-agro-body">
                  <p className="station-agro-title">Grados día acumulados</p>
                  <p className="station-agro-value">
                    {Math.round(agro.degreeDays.total).toLocaleString('es-AR')}
                    <small> °Cd</small>
                  </p>
                  <p className="station-agro-detail">
                    {agro.degreeDays.countedDays} días desde el {shortDate(agro.since)}
                    {agro.degreeDays.last7Avg !== null && ` · ${round1(agro.degreeDays.last7Avg)} °Cd/día la última semana`}
                  </p>
                  {agro.degreeDays.missingDays > 0 && (
                    <p className="station-agro-detail level-caution">
                      {agro.degreeDays.missingDays === 1
                        ? '1 día sin datos suficientes no suma'
                        : `${agro.degreeDays.missingDays} días sin datos suficientes no suman`}
                      : el acumulado real es mayor.
                    </p>
                  )}
                </div>
              </div>
            )}

            {hasAir && (
              <div className="station-agro-block metric-humidity">
                <span className="station-agro-icon">
                  <IonIcon icon={waterOutline} />
                </span>
                <div className="station-agro-body">
                  <p className="station-agro-title">Horas de hoja mojada</p>
                  <p className="station-agro-value">
                    {wetHoursOf(agro, 0) ?? '—'}
                    <small> h hoy</small>
                  </p>
                  <p className="station-agro-detail">
                    Ayer {wetHoursOf(agro, 1) ?? '—'} h · últimos 7 días {wetTotal7} h. Muchas horas favorecen royas,
                    fusariosis y manchas.
                  </p>
                  <Sparkline values={wetDays.map((d) => d.wetHours ?? 0)} color="var(--domiot-metric-humidity)" bars />
                  <p className="station-agro-caption">Últimos {WET_DAYS_SHOWN} días</p>
                </div>
              </div>
            )}

            {hasAir && (
              <div className="station-agro-block metric-soil">
                <span className="station-agro-icon">
                  <IonIcon icon={swapVerticalOutline} />
                </span>
                <div className="station-agro-body">
                  <p className="station-agro-title">Balance de agua</p>
                  {agro.water.needsLocation ? (
                    <p className="station-agro-detail">
                      Para estimar cuánta agua pierde el cultivo hace falta la ubicación de la estación (o de su campo).
                    </p>
                  ) : agro.water.days === 0 ? (
                    <p className="station-agro-detail">Todavía no hay días completos para calcularlo.</p>
                  ) : (
                    <>
                      {agro.water.balance !== null ? (
                        <p className={`station-agro-value ${agro.water.balance < 0 ? 'is-negative' : 'is-positive'}`}>
                          {signed(agro.water.balance)}
                          <small> mm</small>
                        </p>
                      ) : (
                        <p className="station-agro-value">
                          {round1(agro.water.et0)}
                          <small> mm evapotranspirados</small>
                        </p>
                      )}
                      <p className="station-agro-detail">
                        {agro.water.balance !== null
                          ? `Lluvia ${round1(agro.water.balance + agro.water.et0)} mm − evapotranspiración ${round1(agro.water.et0)} mm en ${agro.water.days} días. `
                          : `En ${agro.water.days} días. Sin pluviómetro no se puede restar la lluvia. `}
                        {agro.water.balance !== null && agro.water.balance < 0 ? 'Al cultivo le está faltando agua. ' : ''}
                        Estimado con la máxima y la mínima (sin sensor de radiación).
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export default StationAgro;
